import { BrwZone, EntityQuery, Query, and, is, or } from '@the-real-insight/tri-model';

import { userAPI } from '../UserAPI';
import { brwColor, nutaLabel } from './brwScale';
import { geometryBounds } from './geometryBounds';

/**
 * The Bodenrichtwertzonen of an area — the surfaces the Gutachterausschuss actually valued.
 *
 * WHY THIS EXISTS NEXT TO THE PARCEL COLOURING
 * --------------------------------------------
 * Painting parcels by `Parcel.brw` answers "what is this plot worth". It cannot answer "what does
 * the market look like here", for a reason that is about numbers rather than taste: the parcel
 * layer is capped at 1,500 per view, and a 5 km view holds 69,395 parcels in Frankfurt and 37,687
 * in Berlin. It shows two percent of what is there, picked by latitude.
 *
 * The same views hold 560 and 197 zones. A zone layer at a ceiling of 800 therefore shows the
 * whole picture where the parcel layer shows a sliver — measured, not assumed:
 *
 *     Frankfurt 5 km    560 zones    69,395 parcels
 *     Berlin    5 km    197 zones    37,687 parcels
 *     Halle     5 km    206 zones    37,012 parcels
 *     Nauen     5 km     34 zones     6,840 parcels
 *
 * They also carry what a parcel summary has to throw away: the zone's own number, the reference
 * plot, the density the value refers to, and the committee that published it.
 */

/**
 * How many zones one view may draw.
 *
 * Above the densest measured 5 km view by a margin, and about 3.4 MB on the wire at ~4.3 kB per
 * zone. The cap matters at the top of the radius slider, not at the bottom; below it nothing is
 * hidden, and at it the caller is told.
 */
export const BRW_ZONE_LIMIT = 800;

export interface BrwZoneResult {
    zones: any[];
    /** True when the ceiling was reached — what is drawn is part of the area, not all of it. */
    truncated: boolean;
    /** The vintages present, e.g. ["2024-01-01"]. More than one means the view crosses a border. */
    stags: string[];
    /** Attribution owed for what is drawn — dl-de/by-2.0 makes naming the source a condition. */
    quellenvermerke: string[];
}

const zonesInBox = async (
    minLat: number,
    maxLat: number,
    minLon: number,
    maxLon: number,
): Promise<any[]> => {
    const filter = and(
        is('centerLat').greaterEqual(minLat),
        is('centerLat').lessEqual(maxLat),
        is('centerLon').greaterEqual(minLon),
        is('centerLon').lessEqual(maxLon),
        // Only Bauland (B), Rohbauland (R), and Entwicklungsland (E) — the agricultural classes
        // are three complete overlapping coverings that would fill the budget and hide building land.
        or(is('entw').equal('B'), is('entw').equal('R'), is('entw').equal('E')),
    );

    const rows = await userAPI.query(
        new Query(new EntityQuery(BrwZone.type, [], undefined, filter)),
        0,
        // One more than the ceiling: that is how the caller learns the box holds more than it
        // shows, without counting it first.
        BRW_ZONE_LIMIT + 1,
    );

    return Array.isArray(rows) ? rows : [];
};

export const loadBrwZonesForArea = async (
    geometry: any,
    /** Middle of the area. Without one the box's own middle stands in. */
    centre?: { lat: number; lon: number } | null,
): Promise<BrwZoneResult> => {
    const bounds = geometryBounds(geometry);
    if (!bounds) {
        return { zones: [], truncated: false, stags: [], quellenvermerke: [] };
    }

    const [[minLon, minLat], [maxLon, maxLat]] = bounds;
    const rows = await zonesInBox(minLat, maxLat, minLon, maxLon);

    if (rows.length <= BRW_ZONE_LIMIT) {
        return { zones: rows, truncated: false, ...describe(rows) };
    }

    /**
     * Too many. The index is scanned by `centerLat` ascending, so what came back is the southern
     * edge of the box over its full width — a band below the area rather than around it. Its
     * height is the useful measurement: it says how much latitude a full load occupies at this
     * density, and a centred window of the same aspect follows. Same reasoning as the parcels and
     * the land use, and for the same reason: the alternative is drawing a strip nobody asked for.
     */
    const latitudes = rows.map((zone: any) => zone?.centerLat).filter(Number.isFinite);
    const span = latitudes.length > 1 ? Math.max(...latitudes) - Math.min(...latitudes) : 0;

    const height = maxLat - minLat;
    const width = maxLon - minLon;
    const centreLat = centre?.lat ?? (minLat + maxLat) / 2;
    const centreLon = centre?.lon ?? (minLon + maxLon) / 2;

    const halfLat = span > 0 ? Math.sqrt(span * height) / 2 : height / 4;
    const halfLon = height > 0 ? halfLat * (width / height) : width / 4;

    const banded = await zonesInBox(
        Math.max(minLat, centreLat - halfLat),
        Math.min(maxLat, centreLat + halfLat),
        Math.max(minLon, centreLon - halfLon),
        Math.min(maxLon, centreLon + halfLon),
    );

    const gezeigt = banded.slice(0, BRW_ZONE_LIMIT);
    return { zones: gezeigt, truncated: true, ...describe(gezeigt) };
};

/** Vintages and attributions of what is on screen — both are properties of the view, not the map. */
const describe = (zones: any[]) => ({
    stags: Array.from(new Set(zones.map((z) => z?.stag).filter(Boolean))).sort(),
    quellenvermerke: Array.from(new Set(zones.map((z) => z?.quellenvermerk).filter(Boolean))).sort(),
});

/**
 * How the map draws one zone.
 *
 * Filled by value, like the parcels, but with a heavier outline and a lower opacity: the zones are
 * the backdrop the parcels are read against, and a zone edge is a statement — it is where the
 * committee decided the market changes.
 *
 * `entw` decides whether it is painted at all. Only Bauland and its two neighbours carry a value
 * in the usual sense; the agricultural classes are three complete, overlapping coverings of the
 * whole state (Brandenburg publishes an Acker, a Grünland and a Forst value for every point) and
 * painting them would put three surfaces on top of each other and hide the building land under a
 * uniform 5 €/m².
 */
export const brwZoneElement = (zone: any): any | null => {
    if (!zone?.geometry) return null;
    if (!['B', 'R', 'E'].includes(String(zone.entw))) return null;

    const format = (value: number) => new Intl.NumberFormat('de-DE').format(Math.round(value));
    const dichte = zone.dichte?.wert != null
        ? `${zone.dichte.art ?? 'Dichte'} ${new Intl.NumberFormat('de-DE', {
              maximumFractionDigits: 2,
          }).format(zone.dichte.wert)}`
        : null;

    // Two lines, same as the parcels: what it is, then what it is worth.
    const tooltip = [
        [zone.zonenIdNativ ? `Zone ${zone.zonenIdNativ}` : null, nutaLabel(zone.nuta)]
            .filter(Boolean)
            .join(' · '),
        [
            zone.brw != null ? `${format(zone.brw)} €/m²` : null,
            dichte,
            // The plot the value is quoted for. Without it a Bodenrichtwert is half a statement.
            zone.flaeBrw ? `Richtwertgrundstück ${format(zone.flaeBrw)} m²` : null,
            zone.stag ? `Stichtag ${zone.stag}` : null,
            zone.gabe ? `Gutachterausschuss ${zone.gabe}` : null,
        ]
            .filter(Boolean)
            .join(' · '),
    ].filter(Boolean);

    return {
        display: {
            type: 'shape',
            shape: zone.geometry,
            color: zone.brw != null ? brwColor(zone.brw) : '#9e9e9e',
            fillColor: zone.brw != null ? brwColor(zone.brw) : '#9e9e9e',
            /**
             * FAINT FILL, STRONG EDGE — and the reason is not taste.
             *
             * The zone layer and the parcel layer paint the SAME quantity from the SAME ramp:
             * `Parcel.brw` is read out of the zone the parcel lies in. Drawn at equal weight they
             * say the same thing twice, and the parcels stop being distinguishable objects inside
             * a single flat colour. The first version filled at 0.35 and did exactly that.
             *
             * Depth order cannot be relied on to fix it. Leaflet appends a layer to the top when
             * it is switched on, whichever order the code inserts it in — so turning the zones on
             * after the parcels puts them above, and turning the parcels on last puts them above.
             * A layer whose readability depends on the order the reader happened to click in is
             * broken either way.
             *
             * So the zones carry their value in the OUTLINE and a wash of fill, the parcels in a
             * solid one. A zone edge is a statement — it is where the committee decided the market
             * changes — and it survives being drawn under or over anything.
             *
             * One pixel, not the 2.5 of the search radius: at that weight the zone edges read as
             * the area frame and swamp the map. The colour carries the value, not the width.
             */
            fillOpacity: 0.14,
            weight: 1,
            opacity: 0.95,
            ...(tooltip.length ? { tooltip: tooltip.length === 1 ? tooltip[0] : tooltip } : {}),
        },
        entity: zone,
    };
};
